import { Grid, Box, Button, Chip, IconButton, MobileStepper, Stack, styled, Typography } from "@mui/material";
import * as React from "react";
import Modal, { CommonModalProps } from "..";
import CloseIcon from "@mui/icons-material/Close";

import { useTranslation } from "react-i18next";
import {
  BookmarkAddOutlined,
  ContentCutOutlined,
  FileDownloadOutlined,
  LogoutOutlined,
  SettingsOutlined,
  Visibility,
  VisibilityOutlined,
} from "@mui/icons-material";

type CloseClbProps = {
  confirm: boolean;
};

export type ModalClipUpdateElementProps = {
  onClose?: (props: CloseClbProps) => void;
};
export type ModalClipUpdateProps = CommonModalProps<ModalClipUpdateElementProps>;

const ModalClipUpdate = React.forwardRef<any, ModalClipUpdateProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, onClose, ...modalProps } = props;
  const [activeStep, setActiveStep] = React.useState(0);

  const handleCancelClick = () => {
    onClose && onClose({ confirm: false });
    closeModal();
  };

  const handleConfirmClick = () => {
    localStorage.setItem("clipUpdateClosedForever", "true");
    onClose && onClose({ confirm: true });
    closeModal();
  };

  const onClickNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const _renderListItem = React.useCallback(
    (icon: React.ReactElement, text: string, colorIcon?: string) => (
      <Stack direction="row" spacing={1.5}>
        {React.cloneElement(icon, { color: colorIcon ? colorIcon : "primary" } as any)}
        <Typography variant="subtitle2" fontWeight={400}>
          {text}
        </Typography>
      </Stack>
    ),
    []
  );

  return (
    <StyledModal
      hideHeader
      {...modalProps}
      title={t("common:onboarding")}
      reference={ref}
      closeModal={handleCancelClick}
      width={720}
      footer={
        <StyledMobileStepper
          position="static"
          variant="dots"
          steps={2}
          activeStep={activeStep}
          backButton={<Button onClick={handleCancelClick}>{t("button:skipForNow")}</Button>}
          nextButton={
            activeStep >= 1 ? (
              <Button variant="contained" color="primary" onClick={handleConfirmClick}>
                {t("common:close")}
              </Button>
            ) : (
              <Button variant="contained" color="primary" onClick={onClickNext}>
                {t("common:next")}
              </Button>
            )
          }
        />
      }
    >
      <Box height="100%">
        <Grid container direction="row" alignItems="flex-end" height="100%">
          <Grid item xs={6} flex={1}>
            <Pictures>
              <StyledFigure active={activeStep === 0}>
                <img src="/static/images/onboarding/clips-1.png" alt="" />
              </StyledFigure>
              <StyledFigure active={activeStep === 1}>
                <img src="/static/images/onboarding/clips-2.png" alt="" />
              </StyledFigure>
            </Pictures>
          </Grid>
          <Grid item xs={6} flex={1} display="flex" flexDirection="column" alignSelf="stretch">
            <Stack pb={2} alignItems="flex-end">
              <IconButton onClick={handleCancelClick}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <Contents pl={6} pr={6} pt={0} pb={6} flex={1}>
              <Content active={activeStep === 0}>
                <Chip
                  size="small"
                  label={t("chip:label.whatsNew")}
                  color="primary"
                  sx={{ textTransform: "uppercase" }}
                />
                <Typography variant="h3" fontWeight={600} mt={2} mb={2}>
                  Nouvelles fonctionnalités sur les Clips !
                </Typography>
                <Stack spacing={2.5}>
                  {_renderListItem(
                    <VisibilityOutlined />,
                    "Les clips disposent d'une vue détail accessible depuis l'onglet Timeline"
                  )}
                  {_renderListItem(
                    <FileDownloadOutlined />,
                    "Leur téléchargement est rendu plus simple, grâce à la création de commandes en un clic."
                  )}
                  {_renderListItem(<BookmarkAddOutlined />, "Vous pouvez également les ajouter à vos collections.")}
                </Stack>
              </Content>
              <Content active={activeStep === 1}>
                <Chip
                  size="small"
                  label={t("chip:label.whatsNew")}
                  color="primary"
                  sx={{ textTransform: "uppercase" }}
                />
                <Typography variant="h3" fontWeight={600} mt={2} mb={2}>
                  Partagez-les grâce aux Accès Externes
                </Typography>
                <Stack spacing={2.5}>
                  {_renderListItem(
                    <LogoutOutlined />,
                    "Ajoutez vos clips à une collection, puis créez un Accès depuis cette collection"
                  )}
                  {_renderListItem(<ContentCutOutlined />, "Vos clients pourront les prévisualiser depuis leur espace")}
                  {_renderListItem(<FileDownloadOutlined />, "Ils pourront également les télécharger en un clic")}
                </Stack>
              </Content>
            </Contents>
          </Grid>
        </Grid>
      </Box>
    </StyledModal>
  );
});

export default ModalClipUpdate;

const StyledModal = styled(Modal)(({ theme }) => ({
  ".MuiModal-body": {
    padding: "0",
    overflow: "hidden",
  },
}));

const Pictures = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr",
  gridTemplateRows: "1fr",
}));
const Contents = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr",
  gridTemplateRows: "1fr",
}));
const Content = styled(Box, { shouldForwardProp: (prop) => prop !== "active" })<{ active: boolean }>(
  ({ theme, active }) => ({
    gridRow: 1,
    gridColumn: 1,
    transition: "opacity .3s",
    ...(!active && { opacity: 0 }),
  })
);

const StyledFigure = styled(Box, { shouldForwardProp: (prop) => prop !== "active" })<{ active: boolean }>(
  ({ theme, active }) => ({
    background: "#101018",
    border: `1px solid ${theme.palette.divider}`,
    borderBottom: "transparent",
    borderRadius: "6px 6px 0 0",
    height: "100%",
    gridRow: 1,
    gridColumn: 1,
    img: {
      ...theme.helpers(theme).fullImage,
    },
    transition: "opacity .3s",
    ...(!active && { opacity: 0 }),
  })
);

const StyledMobileStepper = styled(MobileStepper)(({ theme }) => ({
  padding: 0,
  background: "transparent",
  ".MuiMobileStepper-dot": {
    width: 10,
    height: 10,
    margin: "0 4px",
  },
}));
